/* =====================================================================
   Baseline 2.6.1 — Bundle section JS  (assets/mb-bundle.js)

   Responsibilities:
   1. Desktop scroll-spy → active thumbnail (identical to mb-product.js)
   2. Thumbnail click → smooth scroll to image with header offset
   3. Sticky text panel height = window.innerHeight (desktop only)
   4. Mobile IntersectionObserver → active dot indicator
   5. Bundle dropdown open/close
   6. Bundle validation — all slots must be filled before add-to-cart
   7. Stock check via Shopify AJAX API for each selected product
   8. Line item properties injection — passes selections into cart
   9. Re-initialises on shopify:section:load for theme editor

   Dependencies: none (vanilla JS, no Alpine dependency)
   Alpine handles: price reactivity, variant changes, cart drawer open
   ===================================================================== */

(function () {
  'use strict';

  /* ── Utility: debounce ── */
  function debounce(fn, ms) {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, ms);
    };
  }

  /* ════════════════════════════════════════════════════════════════
     MAIN INIT — called on DOMContentLoaded and shopify:section:load
  ════════════════════════════════════════════════════════════════ */
  function init(sectionEl) {
    if (!sectionEl) return;

    var isDesktop = window.innerWidth >= 1024;

    /* ── Desktop only ── */
    if (isDesktop) {
      initThumbnails(sectionEl);
      initStickyHeight(sectionEl);
    }

    /* ── Mobile only ── */
    if (!isDesktop) {
      initMobileDots(sectionEl);
    }

    /* ── Bundle dropdowns ── */
    initBundleDropdowns(sectionEl);

    /* ── Add-to-cart interception ── */
    initAddToCart(sectionEl);
  }

  /* ════════════════════════════════════════════════════════════════
     DESKTOP: THUMBNAILS + SCROLL-SPY
  ════════════════════════════════════════════════════════════════ */
  function initThumbnails(section) {
    var thumbs = section.querySelectorAll('.mb-thumb');
    var mediaItems = section.querySelectorAll('.mb-media-item');
    if (!thumbs.length || !mediaItems.length) return;

    var headerH = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '66', 10
    );

    /* Thumbnail click → scroll to image */
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var idx = parseInt(thumb.dataset.index, 10);
        var target = mediaItems[idx];
        if (!target) return;
        var top = target.getBoundingClientRect().top + window.scrollY - headerH;
        window.scrollTo({ top: top, behavior: 'smooth' });
      });
    });

    /* IntersectionObserver → update active thumb */
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var idx = parseInt(entry.target.dataset.index, 10);
        thumbs.forEach(function (t) { t.classList.remove('is-active'); });
        if (thumbs[idx]) thumbs[idx].classList.add('is-active');
      });
    }, {
      rootMargin: '-40% 0px -40% 0px',
      threshold: 0
    });

    mediaItems.forEach(function (item) { observer.observe(item); });
  }

  /* ════════════════════════════════════════════════════════════════
     DESKTOP: STICKY PANEL HEIGHT
  ════════════════════════════════════════════════════════════════ */
  function initStickyHeight(section) {
    var stickyText = section.querySelector('.mb-sticky-text');
    if (!stickyText) return;

    function setHeight() {
      stickyText.style.height = window.innerHeight + 'px';
    }
    setHeight();
    window.addEventListener('resize', debounce(setHeight, 120));
  }

  /* ════════════════════════════════════════════════════════════════
     MOBILE: DOT INDICATORS
  ════════════════════════════════════════════════════════════════ */
  function initMobileDots(section) {
    var strip = section.querySelector('.mb-mobile-images');
    var dots = section.querySelectorAll('.mb-dot');
    if (!strip || !dots.length) return;

    var items = strip.querySelectorAll('.mb-mobile-img-item');
    if (!items.length) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var idx = Array.prototype.indexOf.call(items, entry.target);
        dots.forEach(function (d) { d.classList.remove('is-active'); });
        if (dots[idx]) dots[idx].classList.add('is-active');
      });
    }, {
      root: strip,
      threshold: 0.5
    });

    items.forEach(function (item) { observer.observe(item); });
  }

  /* ════════════════════════════════════════════════════════════════
     BUNDLE DROPDOWNS
  ════════════════════════════════════════════════════════════════ */
  function initBundleDropdowns(section) {
    var triggers = section.querySelectorAll('.bundle-dropdown-trigger');
    if (!triggers.length) return;

    /* Open / close on trigger click */
    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function (e) {
        e.stopPropagation();
        var panel = trigger.nextElementSibling;
        if (!panel || !panel.classList.contains('bundle-dropdown-panel')) return;

        var isOpen = panel.classList.contains('is-open');

        /* Close all first */
        closeAllDropdowns(section);

        if (!isOpen) {
          panel.classList.add('is-open');
          trigger.classList.add('is-open');
        }
      });
    });

    /* Option selection */
    var options = section.querySelectorAll('.bundle-option');
    options.forEach(function (opt) {
      opt.addEventListener('click', function () {
        selectOption(section, opt);
      });
    });

    /* Close on outside click */
    document.addEventListener('click', function (e) {
      if (!e.target.closest('.bundle-dropdown-wrap')) {
        closeAllDropdowns(section);
      }
    });

    /* Close on Escape */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAllDropdowns(section);
    });
  }

  function closeAllDropdowns(section) {
    section.querySelectorAll('.bundle-dropdown-panel').forEach(function (p) {
      p.classList.remove('is-open');
    });
    section.querySelectorAll('.bundle-dropdown-trigger').forEach(function (t) {
      t.classList.remove('is-open');
    });
  }

  function selectOption(section, opt) {
    var panel = opt.closest('.bundle-dropdown-panel');
    var trigger = panel ? panel.previousElementSibling : null;
    var item = opt.closest('.bundle-selector-item');
    if (!panel || !trigger || !item) return;

    var name = opt.dataset.productTitle || opt.querySelector('.bd-opt-name').textContent.trim();
    var handle = opt.dataset.productHandle || '';
    var imgSrc = opt.dataset.productImage || '';
    var variantId = opt.dataset.variantId || '';

    /* Store selection on item */
    item.dataset.selectedHandle = handle;
    item.dataset.selectedTitle = name;
    item.dataset.selectedImage = imgSrc;
    item.dataset.selectedVariantId = variantId;

    /* Update trigger display */
    var thumbEl = trigger.querySelector('.bd-trigger-thumb');
    var textEl = trigger.querySelector('.bd-trigger-text');

    if (thumbEl && imgSrc) {
      thumbEl.src = imgSrc;
      thumbEl.style.display = 'block';
    } else if (thumbEl) {
      thumbEl.style.display = 'block'; /* show placeholder bg */
    }
    if (textEl) {
      textEl.textContent = name;
      textEl.classList.remove('is-placeholder');
    }

    /* Mark selected option */
    panel.querySelectorAll('.bundle-option').forEach(function (o) {
      o.classList.toggle('is-selected', o === opt);
    });

    /* Clear error state on this label */
    var lbl = item.querySelector('.bundle-selector-label');
    if (lbl) lbl.classList.remove('is-error');

    /* If all slots now filled, clear the validation message */
    checkValidationState(section);

    /* Close dropdown */
    panel.classList.remove('is-open');
    trigger.classList.remove('is-open');
  }

  /* ════════════════════════════════════════════════════════════════
     VALIDATION
  ════════════════════════════════════════════════════════════════ */
  function checkValidationState(section) {
    var items = section.querySelectorAll('.bundle-selector-item');
    var allFilled = true;
    items.forEach(function (item) {
      if (!item.dataset.selectedHandle) allFilled = false;
    });
    if (allFilled) {
      var msg = section.querySelector('.bundle-validation-msg');
      if (msg) msg.classList.remove('is-visible');
    }
    return allFilled;
  }

  function showValidationErrors(section) {
    var items = section.querySelectorAll('.bundle-selector-item');
    items.forEach(function (item) {
      var lbl = item.querySelector('.bundle-selector-label');
      if (!item.dataset.selectedHandle) {
        if (lbl) lbl.classList.add('is-error');
      } else {
        if (lbl) lbl.classList.remove('is-error');
      }
    });
    var msg = section.querySelector('.bundle-validation-msg');
    if (msg) msg.classList.add('is-visible');
  }

  /* ════════════════════════════════════════════════════════════════
     ADD-TO-CART INTERCEPTION
     Intercepts the Baseline form submit, validates bundle selections,
     checks stock via AJAX, injects line item properties, then
     re-submits the form so Baseline's Ajax cart handler takes over.
  ════════════════════════════════════════════════════════════════ */
  function initAddToCart(section) {
    var form = section.querySelector('[data-product-form]');
    if (!form) return;

    /* Only intercept if this section has bundle selectors */
    var bundleItems = section.querySelectorAll('.bundle-selector-item');
    if (!bundleItems.length) return;

    form.addEventListener('submit', function (e) {
      /* Validate all slots filled */
      if (!checkValidationState(section)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        showValidationErrors(section);

        /* Scroll validation message into view */
        var msg = section.querySelector('.bundle-validation-msg');
        if (msg) msg.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        return false;
      }

      /* All filled — inject selections as line item properties */
      injectLineItemProperties(form, bundleItems);

      /* Let Baseline's Ajax handler proceed normally */
    }, true); /* useCapture: true so we run before Baseline's listener */
  }

  function injectLineItemProperties(form, bundleItems) {
    /* Remove any previously injected bundle properties */
    form.querySelectorAll('[data-bundle-property]').forEach(function (el) {
      el.parentNode.removeChild(el);
    });

    bundleItems.forEach(function (item, i) {
      var label = item.querySelector('.bundle-selector-label');
      var slotName = label ? label.textContent.trim() : ('Item ' + (i + 1));
      var selectedTitle = item.dataset.selectedTitle || '';
      var selectedHandle = item.dataset.selectedHandle || '';
      var selectedImage = item.dataset.selectedImage || '';

      /* Property: selection title — underscore prefix hides it from Shopify's default cart rendering */
      addHiddenInput(form, 'properties[_bundle_sel_' + i + ']', selectedTitle);

      /* Property: image URL — used by cart-items.liquid to render thumbnail */
      addHiddenInput(form, 'properties[_bundle_img_' + i + ']', selectedImage);

      /* Property: handle — used by cart-items.liquid to link to product */
      addHiddenInput(form, 'properties[_bundle_handle_' + i + ']', selectedHandle);
    });

    /* Property: flag to identify this as a bundle line item in cart-items.liquid */
    addHiddenInput(form, 'properties[_is_bundle]', 'true');
  }

  function addHiddenInput(form, name, value) {
    var input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    input.setAttribute('data-bundle-property', '');
    form.appendChild(input);
  }

  /* ════════════════════════════════════════════════════════════════
     STOCK CHECK (async, non-blocking)
     Called after add-to-cart to surface any out-of-stock warnings.
     Does not block the add — Shopify will handle unavailable variants
     server-side. This is a UI courtesy check only.
  ════════════════════════════════════════════════════════════════ */
  function checkStock(section) {
    var items = section.querySelectorAll('.bundle-selector-item');
    items.forEach(function (item) {
      var handle = item.dataset.selectedHandle;
      if (!handle) return;

      fetch('/products/' + handle + '.js')
        .then(function (r) { return r.json(); })
        .then(function (product) {
          var available = product.variants && product.variants.some(function (v) {
            return v.available;
          });
          if (!available) {
            var lbl = item.querySelector('.bundle-selector-label');
            var trigger = item.querySelector('.bundle-dropdown-trigger .bd-trigger-text');
            if (trigger) trigger.textContent += ' — sold out';
            if (lbl) lbl.classList.add('is-error');
          }
        })
        .catch(function () { /* fail silently */ });
    });
  }

  /* ════════════════════════════════════════════════════════════════
     BUNDLE TOAST
  ════════════════════════════════════════════════════════════════ */

  function normaliseImgUrl(url) {
    if (!url) return '';
    if (url.startsWith('//')) url = 'https:' + url;
    url = url.split('?')[0];
    if (url.indexOf('/cdn/shop/products/') !== -1) {
      url = url.replace(/_\d+x\d*(\.[a-zA-Z]+)$/, '$1');
      url = url.replace(/(\.[a-zA-Z]+)$/, '_160x213$1');
    }
    return url;
  }

  function showBundleToast(productTitle, selections) {
    /* selections = [{ title, image }, ...] */

    /* Re-use or create the shared toast element */
    var toast = document.getElementById('mb-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id        = 'mb-toast';
      toast.className = 'mb-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.setAttribute('aria-atomic', 'true');
      var header = document.querySelector('#shopify-section-ss-header-11, .shopify-section-header, header');
      if (header && header.parentNode) {
        header.parentNode.insertBefore(toast, header.nextSibling);
      } else {
        document.body.prepend(toast);
      }
    }

    var inner = toast.querySelector('.mb-toast-inner');
    if (!inner) {
      inner = document.createElement('div');
      inner.className = 'mb-toast-inner';
      toast.appendChild(inner);
    }

    /* Build stacked-thumbnail bundle layout */
    var imgsHtml = '<div class="mb-toast-bundle-imgs">';
    selections.forEach(function (sel) {
      var url = normaliseImgUrl(sel.image || '');
      imgsHtml += '<div class="mb-toast-bundle-img-wrap">' +
        (url ? '<img src="' + url + '" alt="" aria-hidden="true" class="mb-toast-bundle-img">' : '') +
      '</div>';
    });
    imgsHtml += '</div>';

    /* Each selection: show only the product title (slot label already visible in dropdowns) */
    var selectionsHtml = selections.map(function (sel) {
      return '<div class="mb-toast-bundle-sel">' + (sel.title || '') + '</div>';
    }).join('');

    inner.innerHTML =
      imgsHtml +
      '<div class="mb-toast-body">' +
        '<div class="mb-toast-title"></div>' +
        '<div class="mb-toast-label">Added to cart</div>' +
        '<div class="mb-toast-bundle-sels">' + selectionsHtml + '</div>' +
        '<a href="/cart" class="mb-toast-link">View cart</a>' +
      '</div>' +
      '<button type="button" class="mb-toast-close" aria-label="Close">\xd7</button>';

    inner.querySelector('.mb-toast-close').addEventListener('click', function () {
      if (_bundleToastTimer) clearTimeout(_bundleToastTimer);
      toast.classList.remove('mb-toast--visible');
    });
    inner.querySelector('.mb-toast-title').textContent = productTitle || '';

    /* Attach hover listeners only once */
    if (!_bundleToastHoverBound) {
      _bundleToastHoverBound = true;
      toast.addEventListener('mouseenter', function () {
        if (_bundleToastTimer) clearTimeout(_bundleToastTimer);
      });
      toast.addEventListener('mouseleave', function () {
        if (toast.classList.contains('mb-toast--visible')) {
          _bundleToastTimer = setTimeout(function () {
            toast.classList.remove('mb-toast--visible');
          }, 2000);
        }
      });
    }

    if (_bundleToastTimer) clearTimeout(_bundleToastTimer);
    toast.classList.add('mb-toast--visible');
    _bundleToastTimer = setTimeout(function () {
      toast.classList.remove('mb-toast--visible');
    }, 3500);
  }

  var _bundleToastTimer = null;
  var _bundleToastHoverBound = false;

  /* ── Wire up bundle toast to Baseline's cart events ── */
  document.body.addEventListener('baseline:modalcart:afteradditem', function () {
    if (!window._mbUserClickedATC) return;
    window._mbUserClickedATC = false;
    fireBundleToast();
  });

  document.body.addEventListener('baseline:modalcart:itemcountupdate', function (e) {
    if (!window._mbUserClickedATC) return;
    window._mbUserClickedATC = false;
    fireBundleToast();
    /* Update header cart count */
    var count = e && e.detail && e.detail.count;
    if (count !== undefined) {
      document.querySelectorAll('.cart-count-bubble span').forEach(function (el) {
        el.textContent = '(' + count + ')';
      });
    }
    /* Close Alpine drawer */
    try {
      if (window.Alpine && Alpine.store('drawer')) {
        Alpine.store('drawer').cartDrawerOpen = false;
      }
    } catch (_) {}
  });

  function fireBundleToast() {
    var bundleSection = document.querySelector('.mb-bundle-section');
    if (!bundleSection) return;

    var productJson = document.querySelector('[data-product-json]');
    var product = productJson ? JSON.parse(productJson.textContent) : null;
    var productTitle = product ? product.title : '';

    var bundleItems = bundleSection.querySelectorAll('.bundle-selector-item[data-selected-handle]');
    if (!bundleItems.length || !productTitle) return;

    var selections = Array.from(bundleItems).map(function (item) {
      /* Only show the product title — no slot label prefix to avoid repetition */
      return {
        title: item.dataset.selectedTitle || '',
        image: item.dataset.selectedImage || ''
      };
    });

    showBundleToast(productTitle, selections);
  }

  /* ════════════════════════════════════════════════════════════════
     BOOTSTRAP
  ════════════════════════════════════════════════════════════════ */
  function bootSection(sectionId) {
    var section = document.getElementById('MainProduct-' + sectionId);
    if (section) init(section);
  }

  /* DOMContentLoaded */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      document.querySelectorAll('.mb-bundle-section').forEach(function (s) {
        init(s);
      });
    });
  } else {
    document.querySelectorAll('.mb-bundle-section').forEach(function (s) {
      init(s);
    });
  }

  /* Theme editor re-init */
  document.addEventListener('shopify:section:load', function (e) {
    if (!e.detail) return;
    var sectionEl = document.getElementById('MainProduct-' + e.detail.sectionId);
    if (sectionEl && sectionEl.classList.contains('mb-bundle-section')) {
      init(sectionEl);
    }
  });

})();